import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Product-line combobox — a real click-to-open dropdown (native <datalist> is
 * unreliable: won't open on click, filters out an already-matching value, isn't
 * testable). Reuse an existing product line, or type a new one and "Create" it.
 *
 * The panel renders in a PORTAL with position:fixed, anchored to the trigger, so
 * it can never be clipped by an ancestor's overflow (the /repos table wrapper has
 * overflow-x:auto, which per spec clips the y-axis too and would trap an absolutely
 * positioned panel inside the table). Being out of flow it also causes NO layout
 * shift. It flips above the trigger when there isn't room below, and follows the
 * trigger on scroll/resize.
 */
const PANEL_W = 220;
const PANEL_MAX = 300;
const GAP = 4;

interface Placement { left: number; top: number; up: boolean; maxHeight: number }

function place(rect: DOMRect): Placement {
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_W - 8));
  const below = window.innerHeight - rect.bottom;
  const above = rect.top;
  const up = below < PANEL_MAX && above > below;
  return {
    left,
    top: up ? rect.top - GAP : rect.bottom + GAP,
    up,
    maxHeight: Math.min(PANEL_MAX, (up ? above : below) - 8),
  };
}

export function ProductLineCombo({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string;
  options: string[];
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const reposition = () => {
    if (triggerRef.current) setPos(place(triggerRef.current.getBoundingClientRect()));
  };
  useLayoutEffect(() => {
    if (open) reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Focus the filter WITHOUT scrolling — a plain autoFocus makes the browser scroll
  // the container to the (fixed, portaled) input, which would fire our scroll
  // reposition against a moved trigger and fling the panel off-screen.
  useEffect(() => {
    if (open) searchRef.current?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => reposition();
    // capture: also catch scroll inside the overflow:auto table wrapper (scroll doesn't bubble)
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('mousedown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const query = q.trim();
  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));
  const exact = options.some((o) => o.toLowerCase() === query.toLowerCase());
  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  const panel =
    open && pos
      ? createPortal(
          <div
            ref={panelRef}
            className="pl-combo-panel"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              width: PANEL_W,
              maxHeight: pos.maxHeight,
              transform: pos.up ? 'translateY(-100%)' : undefined,
              zIndex: 1000,
            }}
          >
            <input
              ref={searchRef}
              className="pl-combo-search"
              placeholder="filter, or type a new line…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && query) pick(query);
                if (e.key === 'Escape') setOpen(false);
              }}
            />
            <div className="pl-combo-list">
              {query && !exact && (
                <button type="button" className="pl-combo-item pl-combo-create" onClick={() => pick(query)}>
                  + Create “{query}”
                </button>
              )}
              {filtered.map((o) => (
                <button type="button" key={o} className={`pl-combo-item${o === value ? ' active' : ''}`} onClick={() => pick(o)}>
                  {o}
                </button>
              ))}
              {!filtered.length && !query && <div className="pl-combo-empty muted">no product lines yet</div>}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="pl-combo">
      <button ref={triggerRef} type="button" className="pl-combo-trigger" disabled={disabled} onClick={() => setOpen((o) => !o)}>
        <span className={value ? '' : 'muted'}>{value || 'pick or type…'}</span>
        <span className="pl-combo-caret" aria-hidden>▾</span>
      </button>
      {panel}
    </div>
  );
}
